import ArticleLayout from '../../components/ArticleLayout';

export default function GammelRadiatorVarmerIkke() {
  return (
    <ArticleLayout
      title="Gammel radiator varmer ikke: Tilstoppet, rusten eller fejlindstillet?"
      metaTitle="Gammel radiator varmer ikke? Slam, rust eller ventil"
      metaDescription="Varmer den gamle radiator ikke? Se forskellen på en fastgroet ældre ventil, indvendigt rustslam i bunden og forkert rørføring i ældre boliger."
      canonicalPath="/gammel-radiator-varmer-ikke"
      introLead="Når en ældre radiator – hvad enten det er en tung støbejernsradiator eller en tidlig stålpanelmodel – nægter at varme, skyldes det typisk ophobet slam i bunden eller en forældet, tilkalket ventil."
      introAnswer="Støbejernsradiatorer holder i årtier, men anlægsvandet efterlader jernoxid (magnetitslam) og kalk i bunden af elementerne. Start med at tjekke ventilen og luftskruen, før du konkluderer, at radiatoren er defekt."
      tableOfContents={[
        { id: 'tjekliste', title: '1. Tjekliste: Fejlfinding på gamle radiatorer' },
        { id: 'ventiltyper', title: '2. Gamle manuelle ventiler mod termostater' },
        { id: 'slam', title: '3. Slam og magnetit i bunden' },
        { id: 'rust', title: '4. Udvendig og indvendig rust' },
        { id: 'udskifte-bevare', title: '5. Skal den bevares eller udskiftes?' },
        { id: 'faq', title: '6. Ofte stillede spørgsmål' },
      ]}
      checklistTitle="Tjekliste: Vurdering af en gammel radiator"
      checklist={[
        {
          title: 'Tjek om ventilen er en ældre manuel drejehane uden termostat',
          description: 'Gamle drejehaner med pakgarn kan gro ubehjælpeligt fast og skal ofte udskiftes til moderne termostater.',
        },
        {
          title: 'Mærk om toppen er varm og bunden helt kold ved fuld åbning',
          description: 'Er toppen skoldhed mens de nederste 15 cm er kolde trods høj rumvarmebehov, er der slam i bunden.',
        },
        {
          title: 'Undersøg luftskruen forsigtigt for gennemtæring',
          description: 'På gamle radiatorer kan luftskruer være rustet fast; anvend aldrig overdreven vold for at undgå brud.',
        },
        {
          title: 'Kontrollér om anlægget er et 1-strenget eller 2-strenget system',
          description: 'I ældre ejendomme med 1-strengs anlæg påvirker naboradiatorernes stilling vandtemperaturen.',
        },
        {
          title: 'Inspicér samlinger og nippelforbindelser for rusttæring',
          description: 'Hvide eller rødbrune krystaller ved rørsamlinger indikerer langsom udsivning og begyndende tæring.',
        },
      ]}
      sections={[
        {
          id: 'ventiltyper',
          title: 'Gamle manuelle ventiler mod moderne termostater',
          content: (
            <>
              <p>
                Mange ældre radiatorer har stadig manuelle skrueventiler (drejegreb med håndhjul), som blot åbner og lukker for gennemstrømningen uden hensyn til rummets temperatur.
              </p>
              <p>
                Disse ventiler er ofte tætnet med pakgarn, som tørrer ud, når ventilen står stille. Monterer man en moderne forindstillelig termostatventil fra fx Danfoss, opnår man både automatisk temperaturstyring og markant lavere varmeregning.
              </p>
            </>
          ),
        },
        {
          id: 'slam',
          title: 'Slam, rust og magnetit i radiatorens bund',
          content: (
            <>
              <p>
                I ældre varmeanlæg med sorte stålrør iltes vandet over tid. Iltningen danner sorte jernoxidpartikler, kendt som magnetit. Dette tunge slam falder til bunds i radiatorens nederste sektioner.
              </p>
              <p>
                Når bunden fyldes med slam, kan vandet kun løbe igennem den øverste tredjedel. Resultatet er stærkt nedsat varmeafgivelse. En VVS-installatør kan gennemskylle radiatoren med rensevæske under tryk eller montere et magnetitfilter på anlægget.
              </p>
            </>
          ),
        },
        {
          id: 'rust',
          title: 'Udvendig rust versus indvendig tæring',
          content: (
            <>
              <p>
                Overfladerust på radiatorer – fx under luftskruen eller ved bunden, hvor gulvvask har fugtet jernet – er oftest harmløst og kan slibes væk og males med varmebestandig radiatormaling.
              </p>
              <p>
                Anderledes forholder det sig med tæring indefra. Hvis der opstår fugtige pletter under malingen, som danner bobler med sort eller brunt vand, er jernet gennemtæret. I så fald skal radiatoren udskiftes omgående for at undgå et pludseligt rørbrud.
              </p>
            </>
          ),
        },
        {
          id: 'udskifte-bevare',
          title: 'Skal den gamle radiator bevares eller udskiftes?',
          content: (
            <>
              <p>
                Støbejernsradiatorer har stor vandvolumen og holder længe på varmen. De passer æstetisk godt til ældre murermestervillaer og herskabslejligheder. Hvis de er tætte og ventilen skiftes, kan de ofte bevares.
              </p>
              <p>
                Skifter man derimod til varmepumpe, kræves ofte større radiatorer med flere konvektorplader (type 22 eller 33) for at kunne opvarme med lavere fremløbstemperatur.
              </p>
            </>
          ),
        },
      ]}
      proCallout={{
        title: 'Hvornår skal VVS-fagmanden håndtere udskiftningen?',
        content: (
          <p>
            Tømning af vand, afmontering af tunge støbejernsradiatorer og gevindtilpasning på gamle jernrør med pakgarn kræver specialværktøj og autorisation. Forsøg aldrig selv at skrue en fastrustet ventil af, da røret i væggen kan knække og forårsage en omfattende oversvømmelse.
          </p>
        ),
      }}
      faq={[
        {
          q: 'Kan man rense en gammel radiator for slam selv?',
          a: 'Gennemskylning kræver, at radiatoren afmonteres helt eller tilsluttes en spulepumpe med afspærringsventiler. Det indebærer stor risiko for vandskade og bør udføres af en VVS-fagmand.',
        },
        {
          q: 'Hvor længe holder en støbejernsradiator?',
          a: 'Støbejernsradiatorer kan holde i mere end 80–100 år, så længe anlægsvandet er iltfrit og der ikke opstår udvendig rust ved utætte ventiler.',
        },
        {
          q: 'Hvad koster det at få en ny ventil på en gammel radiator?',
          a: 'Udskiftning af en radiatorventil med ny termostat koster typisk 1.200–2.500 kr. inklusiv moms og montering, afhængigt af om anlægget skal tappes for vand.',
        },
      ]}
      internalLinks={[
        { to: '/radiator-varmer-ikke', text: 'Radiatoren varmer ikke', desc: 'den primære fejlsøgningstjekliste' },
        { to: '/radiator-ventil-sidder-fast', text: 'Ventilstiften sidder fast', desc: 'løsning af ældre ventiler' },
        { to: '/radiator-udskiftning-pris', text: 'Pris på udskiftning af radiator', desc: 'overslag over udgifter' },
        { to: '/radiator-og-varmepumpe', text: 'Radiatorer og varmepumpe', desc: 'hvorfor ældre radiatorer kan være for små' },
      ]}
      sources={[
        { url: 'https://www.bolius.dk/valg-af-radiator-19095', label: 'Valg af radiator - sådan sikrer du, at du vælger rigtigt' },
        { url: 'https://www.danfoss.com/da-dk/products/dhs/radiator-and-room-thermostats/radiator-thermostats/mechanical-radiator-thermostats/', label: 'Danfoss Aveo™ mekanisk radiatortermostat | Danfoss' },
      ]}
    />
  );
}
